import { useState } from "react";
import { MapPin, Phone, Clock, Mail, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CONTACT, HOURS } from "@/data/site";
import { Reveal } from "@/components/site/Reveal";
import { submitContact } from "@/lib/api";

const inputClass =
  "bg-[#1C2541] border-white/10 text-[#F8F5F2] placeholder:text-[#8E94A3] h-12 focus-visible:ring-primary";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill out every field.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Thanks! We'll be in touch soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Something went wrong. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#1C2541] py-16 md:py-24"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
          <div>
            <Reveal>
              <span className="font-label text-xs text-primary">Come See Us</span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-display text-5xl md:text-7xl text-[#F8F5F2]">
                Pull up a <span className="text-primary">stool.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-md text-[#F8F5F2]/75 text-base leading-relaxed">
                Questions or band bookings — send us a note or swing by.
              </p>
            </Reveal>

            <div className="mt-10 space-y-6">
              <a
                data-testid="contact-address"
                href={CONTACT.mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-4 group"
              >
                <span className="mt-1 grid h-11 w-11 place-items-center rounded-full bg-primary/15 text-primary shrink-0">
                  <MapPin className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-label text-[11px] text-[#8E94A3]">
                    Find Us
                  </span>
                  <span className="block text-[#F8F5F2] group-hover:text-primary transition-colors">
                    {CONTACT.address}, {CONTACT.city}
                  </span>
                </span>
              </a>

              <a
                data-testid="contact-phone"
                href={CONTACT.phoneHref}
                className="flex items-start gap-4 group"
              >
                <span className="mt-1 grid h-11 w-11 place-items-center rounded-full bg-primary/15 text-primary shrink-0">
                  <Phone className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-label text-[11px] text-[#8E94A3]">
                    Call Us
                  </span>
                  <span className="block text-[#F8F5F2] group-hover:text-primary transition-colors">
                    {CONTACT.phone}
                  </span>
                </span>
              </a>

              <div className="flex items-start gap-4">
                <span className="mt-1 grid h-11 w-11 place-items-center rounded-full bg-primary/15 text-primary shrink-0">
                  <Clock className="h-5 w-5" />
                </span>
                <div>
                  <span className="block font-label text-[11px] text-[#8E94A3]">
                    Hours
                  </span>
                  <ul className="mt-1 space-y-1">
                    {HOURS.map((h) => (
                      <li key={h.day} className="text-[#F8F5F2] text-sm">
                        <span className="text-[#8E94A3]">{h.day}</span> — {h.time}
                      </li>
                    ))}
                  </ul>
                  <p
                    data-testid="age-notice"
                    className="mt-3 inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 font-label text-[10px] text-primary tracking-[0.22em]"
                  >
                    21+ after 7 PM
                  </p>
                </div>
              </div>

              <a
                data-testid="contact-email"
                href={CONTACT.emailHref}
                className="flex items-start gap-4 group"
              >
                <span className="mt-1 grid h-11 w-11 place-items-center rounded-full bg-primary/15 text-primary shrink-0">
                  <Mail className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-label text-[11px] text-[#8E94A3]">
                    Email
                  </span>
                  <span className="block text-[#F8F5F2] group-hover:text-primary transition-colors break-all">
                    {CONTACT.email}
                  </span>
                </span>
              </a>
            </div>
          </div>

          <Reveal delay={0.1}>
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="rounded-md border border-white/10 bg-[#0B132B] p-7 md:p-10"
            >
              <div className="space-y-5">
                <div>
                  <Label htmlFor="name" className="text-[#F8F5F2] font-label text-[11px]">
                    Name
                  </Label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className={`mt-2 ${inputClass}`}
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[#F8F5F2] font-label text-[11px]">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-email-input"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className={`mt-2 ${inputClass}`}
                  />
                </div>
                <div>
                  <Label htmlFor="message" className="text-[#F8F5F2] font-label text-[11px]">
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us what's up..."
                    className={`mt-2 min-h-[140px] ${inputClass}`}
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={loading}
                  className="w-full rounded-full h-12 font-label text-xs hover:-translate-y-0.5 active:scale-95 transition-transform"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Send Message <Send className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
